import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { 
  Battery, BatteryLow, BatteryMedium, BatteryFull, BatteryWarning,
  AlertTriangle, TrendingUp, TrendingDown, Minus, Users, Clock, 
  Calendar, Bell, CheckCircle, RefreshCw, HelpCircle, Info
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { GeminiHeader } from '@/components/GeminiHeader';
import { motion } from 'framer-motion';

interface FatigueWorker {
  id: string;
  display_name: string;
  team: string | null;
  position: string | null;
  fatigue_risk_score: number | null;
  consecutive_days_worked: number | null;
  rolling_7day_hours: number | null;
  fatigue_trend: string | null;
  last_activity_at: string | null;
}

interface FatigueAlert {
  id: string;
  worker_id: string;
  alert_type: string;
  severity: string;
  message: string;
  context: any;
  acknowledged_at: string | null;
  created_at: string;
  worker?: {
    display_name: string;
    team: string | null;
  };
}

// Human-readable fatigue interpretations
const fatigueInterpretations = {
  score: [
    { max: 29, label: "Segar", labelEn: "Fresh", color: "bg-emerald-500", textColor: "text-emerald-700", description: "Energi cukup, kondisi fit untuk bekerja" },
    { max: 49, label: "Normal", labelEn: "Normal", color: "bg-lime-500", textColor: "text-lime-700", description: "Tingkat kelelahan wajar, tetap waspada" },
    { max: 69, label: "Lelah", labelEn: "Tired", color: "bg-amber-500", textColor: "text-amber-700", description: "Butuh istirahat tambahan, hindari tugas berat" },
    { max: 100, label: "Sangat Lelah", labelEn: "Very Tired", color: "bg-red-500", textColor: "text-red-700", description: "Risiko tinggi, perlu penanganan segera" }
  ],
  trend: {
    increasing: { label: "Meningkat", description: "Kelelahan meningkat dalam 7 hari terakhir", icon: TrendingUp, color: "text-red-500" },
    decreasing: { label: "Menurun", description: "Kondisi membaik, kelelahan berkurang", icon: TrendingDown, color: "text-emerald-500" },
    stable: { label: "Stabil", description: "Tingkat kelelahan konsisten", icon: Minus, color: "text-muted-foreground" }
  },
  consecutiveDays: {
    safe: { max: 5, label: "Aman", color: "text-emerald-600" },
    warning: { max: 6, label: "Perlu Perhatian", color: "text-amber-600" },
    danger: { max: Infinity, label: "Wajib Istirahat", color: "text-red-600" }
  }
};

const getScoreInterpretation = (score: number | null) => {
  if (score === null || score === 0) return null;
  return fatigueInterpretations.score.find(s => score <= s.max) || fatigueInterpretations.score[3];
};

const getTrendInterpretation = (trend: string | null) => {
  if (!trend || trend === 'stable') return fatigueInterpretations.trend.stable;
  return fatigueInterpretations.trend[trend as keyof typeof fatigueInterpretations.trend] || fatigueInterpretations.trend.stable;
};

const getConsecutiveDaysStatus = (days: number) => {
  if (days <= 5) return fatigueInterpretations.consecutiveDays.safe;
  if (days <= 6) return fatigueInterpretations.consecutiveDays.warning;
  return fatigueInterpretations.consecutiveDays.danger;
};

interface AdminFatigueManagementProps {
  siteId?: string | null;
}

const AdminFatigueManagement = ({ siteId: propSiteId }: AdminFatigueManagementProps = {}) => {
  const [workers, setWorkers] = useState<FatigueWorker[]>([]);
  const [alerts, setAlerts] = useState<FatigueAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState<string>(propSiteId || 'all');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (propSiteId) setSelectedSite(propSiteId);
  }, [propSiteId]);

  useEffect(() => {
    loadFatigueData();
  }, [selectedSite]);

  const loadFatigueData = async () => {
    setIsLoading(true);
    try {
      let workersQuery = supabase
        .from('worker_profiles')
        .select('id, display_name, team, position, fatigue_risk_score, consecutive_days_worked, rolling_7day_hours, fatigue_trend, last_activity_at')
        .order('fatigue_risk_score', { ascending: false, nullsFirst: false });

      if (selectedSite !== 'all') {
        workersQuery = workersQuery.eq('site_id', selectedSite);
      }

      const { data: workersData } = await workersQuery.limit(100);
      setWorkers(workersData || []);

      let alertsQuery = supabase
        .from('fatigue_alerts')
        .select(`
          id, worker_id, alert_type, severity, message, context, acknowledged_at, created_at,
          worker:worker_profiles!worker_id(display_name, team)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (selectedSite !== 'all') {
        alertsQuery = alertsQuery.eq('site_id', selectedSite);
      }

      const { data: alertsData } = await alertsQuery;
      setAlerts((alertsData as any) || []);
    } catch (error) {
      console.error('Error loading fatigue data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId: string) => {
    const { data: profile } = await supabase
      .from('worker_profiles')
      .select('id')
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
      .single();

    await supabase
      .from('fatigue_alerts')
      .update({ 
        acknowledged_at: new Date().toISOString(),
        acknowledged_by: profile?.id
      })
      .eq('id', alertId);

    loadFatigueData();
  };

  const getFatigueIcon = (score: number | null) => {
    if (!score || score < 30) return <BatteryFull className="h-5 w-5 text-emerald-500" />;
    if (score < 50) return <BatteryMedium className="h-5 w-5 text-lime-500" />;
    if (score < 70) return <BatteryLow className="h-5 w-5 text-amber-500" />;
    return <BatteryWarning className="h-5 w-5 text-red-500" />;
  };

  const getFatigueColor = (score: number | null) => {
    if (!score || score < 30) return 'bg-emerald-500/10 text-emerald-700 border-emerald-200';
    if (score < 50) return 'bg-lime-500/10 text-lime-700 border-lime-200';
    if (score < 70) return 'bg-amber-500/10 text-amber-700 border-amber-200';
    return 'bg-red-500/10 text-red-700 border-red-200';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  // Stats
  const workersWithData = workers.filter(w => w.fatigue_risk_score !== null && w.fatigue_risk_score > 0);
  const workersWithoutData = workers.filter(w => w.fatigue_risk_score === null || w.fatigue_risk_score === 0);
  const highFatigueCount = workers.filter(w => (w.fatigue_risk_score || 0) >= 70).length;
  const mediumFatigueCount = workers.filter(w => {
    const score = w.fatigue_risk_score || 0;
    return score >= 50 && score < 70;
  }).length;
  const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged_at).length;
  const consecutiveHighCount = workers.filter(w => (w.consecutive_days_worked || 0) >= 6).length;

  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader title="Fatigue Management" showBack />
      
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Battery className="h-6 w-6 text-primary" />
              Monitor Risiko Kelelahan
            </h1>
            <p className="text-muted-foreground">
              Pantau dan kelola tingkat kelelahan pekerja secara real-time
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!propSiteId && <SiteFilter value={selectedSite} onChange={setSelectedSite} />}
            <Button variant="outline" onClick={loadFatigueData} disabled={isLoading}>
              <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Legend Card */}
        <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              Panduan Interpretasi Skor Kelelahan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {fatigueInterpretations.score.map((level, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-background/80">
                  <div className={`w-3 h-3 rounded-full ${level.color}`} />
                  <div>
                    <p className={`text-sm font-semibold ${level.textColor}`}>
                      {idx === 0 ? "0-29" : idx === 1 ? "30-49" : idx === 2 ? "50-69" : "70-100"}: {level.label}
                    </p>
                    <p className="text-xs text-muted-foreground">{level.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/30 dark:to-red-900/20 border-red-200 dark:border-red-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <BatteryWarning className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700 dark:text-red-400">{highFatigueCount}</p>
                  <p className="text-xs text-red-600/70">Sangat Lelah (70+)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border-amber-200 dark:border-amber-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <BatteryLow className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{mediumFatigueCount}</p>
                  <p className="text-xs text-amber-600/70">Lelah (50-69)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-950/30 dark:to-orange-900/20 border-orange-200 dark:border-orange-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <Bell className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">{unacknowledgedAlerts}</p>
                  <p className="text-xs text-orange-600/70">Alert Aktif</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200 dark:border-purple-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Calendar className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{consecutiveHighCount}</p>
                  <p className="text-xs text-purple-600/70">6+ Hari Berturut</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="workers">Pekerja</TabsTrigger>
            <TabsTrigger value="alerts">
              Alerts
              {unacknowledgedAlerts > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                  {unacknowledgedAlerts}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* Workers without data notice */}
            {workersWithoutData.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800 dark:text-blue-300">
                        {workersWithoutData.length} pekerja belum memiliki data kelelahan
                      </p>
                      <p className="text-sm text-blue-600/80 dark:text-blue-400/80 mt-1">
                        Data kelelahan dihitung setelah pekerja melakukan check-in shift. Pekerja yang belum check-in hari ini akan menampilkan skor 0.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* High Risk Workers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Pekerja Sangat Lelah
                </CardTitle>
                <CardDescription>
                  Pekerja dengan skor kelelahan ≥70 memerlukan perhatian segera
                </CardDescription>
              </CardHeader>
              <CardContent>
                {workers.filter(w => (w.fatigue_risk_score || 0) >= 70).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <BatteryFull className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                    <p className="font-medium">Tidak ada pekerja dengan kelelahan tinggi</p>
                    <p className="text-sm">Semua pekerja dalam kondisi baik</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {workers
                      .filter(w => (w.fatigue_risk_score || 0) >= 70)
                      .slice(0, 5)
                      .map((worker, idx) => {
                        const interpretation = getScoreInterpretation(worker.fatigue_risk_score);
                        const trendInfo = getTrendInterpretation(worker.fatigue_trend);
                        const TrendIcon = trendInfo.icon;
                        const daysStatus = getConsecutiveDaysStatus(worker.consecutive_days_worked || 0);
                        
                        return (
                          <motion.div
                            key={worker.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            className="flex items-center gap-4 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800"
                          >
                            {getFatigueIcon(worker.fatigue_risk_score)}
                            <div className="flex-1">
                              <p className="font-medium">{worker.display_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {worker.team || 'No team'} • 
                                <span className={daysStatus.color}> {worker.consecutive_days_worked || 0} hari berturut ({daysStatus.label})</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-2">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Badge className={`${interpretation?.color} text-white`}>
                                        {worker.fatigue_risk_score} - {interpretation?.label}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{interpretation?.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <TrendIcon className={`h-4 w-4 ${trendInfo.color}`} />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{trendInfo.label}: {trendInfo.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {worker.rolling_7day_hours || 0}h minggu ini {(worker.rolling_7day_hours || 0) >= 48 && <span className="text-amber-600">(melebihi batas)</span>}
                              </p>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bell className="h-5 w-5 text-orange-500" />
                  Alert Terbaru
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                    <p className="font-medium">Tidak ada alert aktif</p>
                    <p className="text-sm">Semua pekerja dalam kondisi normal</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {alerts.slice(0, 5).map((alert) => (
                      <div
                        key={alert.id}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg border",
                          alert.acknowledged_at
                            ? "bg-muted/30 border-border"
                            : "bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800"
                        )}
                      >
                        <div className={cn("w-2 h-2 rounded-full mt-2", getSeverityColor(alert.severity))} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">
                            {(alert.worker as any)?.display_name || 'Unknown'}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {alert.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(alert.created_at).toLocaleString('id-ID')}
                          </p>
                        </div>
                        {!alert.acknowledged_at && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => acknowledgeAlert(alert.id)}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="workers" className="mt-6">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Pekerja</th>
                        <th className="text-center p-4 font-medium">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="flex items-center gap-1 mx-auto">
                                Skor Risiko
                                <HelpCircle className="h-3 w-3" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Skor 0-100 menunjukkan tingkat kelelahan</p>
                                <p className="text-xs text-muted-foreground">0-29: Segar | 30-49: Normal | 50-69: Lelah | 70+: Sangat Lelah</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </th>
                        <th className="text-center p-4 font-medium">Status</th>
                        <th className="text-center p-4 font-medium">Trend</th>
                        <th className="text-center p-4 font-medium">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="flex items-center gap-1 mx-auto">
                                Hari Berturut
                                <HelpCircle className="h-3 w-3" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Jumlah hari kerja berturut-turut tanpa libur</p>
                                <p className="text-xs text-muted-foreground">Rekomendasi: maksimal 6 hari</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </th>
                        <th className="text-center p-4 font-medium">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="flex items-center gap-1 mx-auto">
                                Jam Mingguan
                                <HelpCircle className="h-3 w-3" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Total jam kerja dalam 7 hari terakhir</p>
                                <p className="text-xs text-muted-foreground">Batas aman: 48 jam/minggu</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {workers.map((worker) => {
                        const interpretation = getScoreInterpretation(worker.fatigue_risk_score);
                        const trendInfo = getTrendInterpretation(worker.fatigue_trend);
                        const TrendIcon = trendInfo.icon;
                        const daysStatus = getConsecutiveDaysStatus(worker.consecutive_days_worked || 0);
                        const hasData = worker.fatigue_risk_score !== null && worker.fatigue_risk_score > 0;
                        
                        return (
                          <tr key={worker.id} className="hover:bg-muted/30">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                {getFatigueIcon(worker.fatigue_risk_score)}
                                <div>
                                  <p className="font-medium">{worker.display_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {worker.team || 'No team'}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              {hasData ? (
                                <Badge className={getFatigueColor(worker.fatigue_risk_score)}>
                                  {worker.fatigue_risk_score}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm italic">Belum check-in</span>
                              )}
                            </td>
                            <td className="p-4 text-center">
                              {hasData && interpretation ? (
                                <Badge className={`${interpretation.color} text-white text-xs`}>
                                  {interpretation.label}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-center gap-1">
                                <TrendIcon className={`h-4 w-4 ${trendInfo.color}`} />
                                <span className="text-sm">
                                  {trendInfo.label}
                                </span>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <span className={cn("font-medium", daysStatus.color)}>
                                {worker.consecutive_days_worked || 0}
                              </span>
                            </td>
                            <td className="p-4 text-center">
                              <span className={cn(
                                "font-medium",
                                (worker.rolling_7day_hours || 0) >= 50 && "text-amber-600"
                              )}>
                                {worker.rolling_7day_hours || 0}h
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alerts" className="mt-6">
            <Card>
              <CardContent className="p-4">
                {alerts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="h-16 w-16 mx-auto mb-4 text-emerald-500" />
                    <p className="text-lg font-medium">Tidak ada alert</p>
                    <p className="text-sm">Semua pekerja dalam kondisi normal</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert, idx) => (
                      <motion.div
                        key={alert.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className={cn(
                          "flex items-start gap-4 p-4 rounded-xl border",
                          alert.acknowledged_at
                            ? "bg-muted/30 border-border"
                            : "bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800"
                        )}
                      >
                        <div className={cn(
                          "w-3 h-3 rounded-full mt-1 flex-shrink-0",
                          getSeverityColor(alert.severity)
                        )} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">
                              {(alert.worker as any)?.display_name || 'Unknown'}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {alert.alert_type.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {alert.message}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>{new Date(alert.created_at).toLocaleString('id-ID')}</span>
                            {alert.acknowledged_at && (
                              <span className="flex items-center gap-1 text-emerald-600">
                                <CheckCircle className="h-3 w-3" />
                                Acknowledged
                              </span>
                            )}
                          </div>
                        </div>
                        {!alert.acknowledged_at && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeAlert(alert.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Acknowledge
                          </Button>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminFatigueManagement;
